import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

function FAQ() {
  return (
    <div className="min-h-screen bg-white font-quicksand">
      <SEO
        title="Frequently Asked Questions"
        description="Answers to common questions about StudyPass RDH's NBDHE board exam prep quizzes, study guides, flashcards, and subscription plans."
        path="/faq"
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-12 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-study-black mb-2">
          Frequently Asked Questions
        </h1>
        <p className="text-study-black text-lg">Everything you need to know about StudyPass</p>
      </div>

      {/* FAQ Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* Getting Started */}
        <FAQSection title="Getting Started">
          <FAQItem
            question="What is StudyPass?"
            answer="StudyPass is an online study platform designed specifically for dental hygiene students preparing for their board exams. We offer practice quizzes, videos, study guides, flashcards, and progress tracking to help you succeed."
          />
          <FAQItem
            question="How do I create an account?"
            answer="Click the 'Sign Up' button on the homepage or navigation bar. You'll need to provide your email address and create a password. Once registered, you can start using the free tier immediately."
          />
          <FAQItem
            question="Is StudyPass only for dental hygiene students?"
            answer="Yes, StudyPass is specifically designed for dental hygiene students preparing for the NBDHE (National Board Dental Hygiene Examination) and written Local Anesthesia board exam."
          />
        </FAQSection>

        {/* Subscription & Billing */}
        <FAQSection title="Subscription & Billing">
          <FAQItem
            question="What's the difference between Free and Premium?"
            answer="Free users can take 1 quiz per day and track their progress. Premium members get unlimited quizzes, access to all videos, study guides, flashcard decks, detailed analytics, and priority support."
          />
          <FAQItem
            question="How much does Premium cost?"
            answer="Premium is $20 per month. You can cancel anytime and will retain access until the end of your billing period."
          />
          <FAQItem
            question="Can I cancel my subscription?"
            answer="Yes! You can cancel your subscription at any time. Go to your Profile page to manage your subscription. You'll continue to have Premium access until the end of your current billing period."
          />
          <FAQItem
            question="What payment methods do you accept?"
            answer="We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor, Stripe."
          />
          <FAQItem
            question="Is there a refund policy?"
            answer="Monthly subscriptions can be canceled anytime but are non-refundable."
          />
        </FAQSection>

        {/* Study Content */}
        <FAQSection title="Study Content">
          <FAQItem
            question="How many practice questions are available?"
            answer="We have thousands of board-style practice questions covering all major topics including Anatomy, Periodontology, Radiology, Pharmacology, Ethics, and more."
          />
          <FAQItem
            question="Are the questions similar to the actual board exam?"
            answer="Yes! Our questions are designed to match the format, style, and difficulty level of the NBDHE. They're created and reviewed by registered dental hygienists."
          />
          <FAQItem
            question="Can I study specific subjects?"
            answer="Absolutely! You can choose to take quizzes on specific subjects or create custom quizzes mixing multiple topics. Visit Essentials to browse all available subjects."
          />
          <FAQItem
            question="How do flashcards work?"
            answer="Our flashcards use a spaced repetition approach. You'll see a term or concept, try to recall the answer, then flip to check. Mark cards as 'Know It' or 'Still Learning' to track your progress."
          />
        </FAQSection>

        {/* Progress & Performance */}
        <FAQSection title="Progress & Performance">
          <FAQItem
            question="How is my progress tracked?"
            answer="We track every quiz you take, including your scores, time spent, and performance by subject. Visit the Progress page to see detailed analytics and identify areas that need more study."
          />
          <FAQItem
            question="Can I see which questions I got wrong?"
            answer="Yes! After completing a quiz, you'll see a detailed results page showing each question, your answer, the correct answer, and explanations where available."
          />
          <FAQItem
            question="Is my data saved if I log out?"
            answer="Yes, all your progress, quiz history, and statistics are saved to your account. You can log in from any device and pick up where you left off."
          />
        </FAQSection>

        {/* Technical */}
        <FAQSection title="Technical Questions">
          <FAQItem
            question="What devices can I use StudyPass on?"
            answer="StudyPass works on any device with a modern web browser - computers, tablets, and smartphones. No app download required."
          />
          <FAQItem
            question="Do I need an internet connection?"
            answer="Yes, StudyPass requires an internet connection to access content, take quizzes, and sync your progress."
          />
          <FAQItem
            question="I forgot my password. How do I reset it?"
            answer="Click 'Sign In' then 'Forgot Password'. Enter your email address and we'll send you a link to reset your password."
          />
        </FAQSection>

        {/* Contact */}
        <FAQSection title="Still Have Questions?">
          <div className="bg-gradient-to-r from-study-yellow to-study-pink rounded-2xl p-8 text-center">
            <p className="text-study-black text-lg mb-4">
              Can't find what you're looking for? We're here to help!
            </p>
            <a
              href="mailto:support@studypassrdh.com"
              className="inline-block bg-study-teal text-study-black font-bold py-3 px-6 rounded-full hover:bg-white transition-all"
            >
              Contact Support
            </a>
          </div>
        </FAQSection>

        {/* Back Link */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="text-study-teal font-bold hover:text-study-pink transition-all"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function FAQSection({ title, children }) {
  return (
    <div className="mb-12">
      <h2 className="text-2xl font-bold text-study-black mb-6 pb-2 border-b-2 border-study-teal">
        {title}
      </h2>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between text-left p-4 transition-all ${
          isOpen ? 'bg-study-teal' : 'bg-white hover:bg-gray-50'
        }`}
      >
        <span className="font-semibold text-study-black pr-4">{question}</span>
        <span className={`text-2xl flex-shrink-0 transition-transform ${isOpen ? 'rotate-45' : ''}`}>
          +
        </span>
      </button>
      {isOpen && (
        <div className="p-4 bg-white border-t border-gray-200">
          <p className="text-gray-700">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default FAQ;
